import React, { useState, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Command, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, MapPin, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  normalizeCity, 
  normalizeSuburb, 
  normalizeProvinceState, 
  normalizePostalCode, 
  normalizeString 
} from '@/lib/addressUtils';
import type { AddressHierarchy } from '@/hooks/useAddressHierarchy';

interface SmartAddressInputProps {
  form: UseFormReturn<any>;
  prefix?: string;
  required?: boolean;
  existingAddresses?: any[]; // Customer data to extract suggestions from
  addressHierarchy: ReturnType<typeof import("@/hooks/useAddressHierarchy").useAddressHierarchy>;
  onAddressValidation?: (field: string, isValid: boolean, suggestions?: string[]) => void;
}

export function SmartAddressInput({ 
  form, 
  prefix = '', 
  required = false, 
  existingAddresses = [],
  addressHierarchy,
  onAddressValidation 
}: SmartAddressInputProps) {
  const getFieldName = (field: string) => prefix ? `${prefix}_${field}` : field;

  // Extract suggestions from existing data (customers)
  const addressSuggestions = useMemo(() => {
    const cities = new Set<string>();
    const suburbs = new Set<string>();
    const provinces = new Set<string>();
    const postalCodes = new Set<string>();
    
    console.log('SmartAddressInput: Processing', existingAddresses.length, 'customers for suggestions');
    
    existingAddresses.forEach(customer => {
      // Handle customer objects with normalized fields
      if (customer.city) cities.add(normalizeCity(customer.city));
      if (customer.suburb) suburbs.add(normalizeSuburb(customer.suburb));
      if (customer.province_state) provinces.add(normalizeProvinceState(customer.province_state));
      if (customer.postal_code) postalCodes.add(normalizePostalCode(customer.postal_code));
    });
    
    const result = {
      cities: Array.from(cities).sort(),
      suburbs: Array.from(suburbs).sort(),
      provinces: Array.from(provinces).sort(),
      postalCodes: Array.from(postalCodes).sort(),
    };
    
    console.log('SmartAddressInput: Generated suggestions:', {
      cities: result.cities.length,
      suburbs: result.suburbs.length,
      provinces: result.provinces.length,
      postalCodes: result.postalCodes.length
    });
    
    return result;
  }, [existingAddresses]);
  // Enhanced suggestions combining existing data with SA address service
  const enhancedSuggestions = useMemo(() => {
    const saProvinces = addressHierarchy.getSuggestions('', 'province').map(s => s.value);
    const saCities = addressHierarchy.getSuggestions('', 'city').map(s => s.value);
    const saSuburbs = addressHierarchy.getSuggestions('', 'suburb').map(s => s.value);
    
    return {
      cities: [...new Set([...addressSuggestions.cities, ...saCities])].sort(),
      suburbs: [...new Set([...addressSuggestions.suburbs, ...saSuburbs])].sort(),
      provinces: [...new Set([...addressSuggestions.provinces, ...saProvinces])].sort(),
      postalCodes: addressSuggestions.postalCodes,
    };
  }, [addressSuggestions, addressHierarchy]);

  // Validation states
  const [validationState, setValidationState] = useState<{
    [field: string]: { isValid: boolean; warnings: string[]; suggestions: string[] }
  }>({});

  // Enhanced validation with SA address service support
  const validateAddressField = (field: string, value: string, allFormValues: any) => {
    const normalized = field === 'city' ? normalizeCity(value) : 
                      field === 'suburb' ? normalizeSuburb(value) : 
                      field === 'province_state' ? normalizeProvinceState(value) : 
                      normalizePostalCode(value);
    
    let isValid = true;
    let warnings: string[] = [];
    let suggestions: string[] = [];
    
    if (!normalized) {
      setValidationState(prev => ({ ...prev, [field]: { isValid: true, warnings: [], suggestions: [] } }));
      return;
    }

    console.log('🔍 Validating field:', field, 'value:', normalized);

    // Postal code validation using SA patterns
    if (field === 'postal_code' && allFormValues.province_state) {
      const isValidPostalCode = addressHierarchy.validatePostalCode(normalized, allFormValues.province_state);
      if (!isValidPostalCode) {
        isValid = false;
        warnings.push(`Postal code ${normalized} doesn't match ${allFormValues.province_state} format`);
      }
    }

    // Cross-validate relationships
    if (field === 'suburb' && allFormValues.city) {
      const parentCity = addressHierarchy.getCityForSuburb(normalized);
      const formCity = normalizeCity(allFormValues.city);
      
      if (parentCity && parentCity !== formCity) {
        isValid = false;
        warnings.push(`"${normalized}" is typically in "${parentCity}", not "${formCity}"`);
        suggestions.push(`Change city to "${parentCity}"`);
      }
    }

    if (field === 'city' && allFormValues.suburb) {
      const expectedCity = addressHierarchy.getCityForSuburb(normalizeSuburb(allFormValues.suburb));
      
      if (expectedCity && expectedCity !== normalized) {
        warnings.push(`Suburb "${allFormValues.suburb}" is typically in "${expectedCity}"`);
        suggestions.push(`Change city to "${expectedCity}"`);
      }
    }

    if (field === 'city' && allFormValues.province_state) {
      const parentProvince = addressHierarchy.getProvinceForCity(normalized);
      const formProvince = normalizeProvinceState(allFormValues.province_state);
      
      if (parentProvince && parentProvince !== formProvince) {
        isValid = false;
        warnings.push(`"${normalized}" is in "${parentProvince}", not "${formProvince}"`);
        suggestions.push(`Change province to "${parentProvince}"`);
      }
    }

    setValidationState(prev => ({ 
      ...prev, 
      [field]: { isValid, warnings, suggestions } 
    }));
    
    onAddressValidation?.(field, isValid, suggestions);
  };

  // Enhanced auto-complete with SA address service
  const handleFieldSelect = (field: string, value: string) => {
    const formFieldName = getFieldName(field) as any;
    form.setValue(formFieldName, value);
    
    console.log(`SmartAddressInput: Selected ${field}:`, value);
    
    // Get auto-completion suggestions from SA address service
    const autoSuggestions = addressHierarchy.getAutoCompletionSuggestions(
      field as 'suburb' | 'city' | 'province', 
      value
    );

    // Auto-populate related fields
    if (field === 'suburb') {
      if (autoSuggestions.city) {
        const currentCity = form.getValues(getFieldName('city') as any);
        if (!currentCity || currentCity !== autoSuggestions.city) {
          form.setValue(getFieldName('city') as any, autoSuggestions.city);
          console.log(`SmartAddressInput: Auto-populated city: ${autoSuggestions.city}`);
        }
      }
      if (autoSuggestions.province) {
        const currentProvince = form.getValues(getFieldName('province_state') as any);
        if (!currentProvince || currentProvince !== autoSuggestions.province) {
          form.setValue(getFieldName('province_state') as any, autoSuggestions.province);
          console.log(`SmartAddressInput: Auto-populated province: ${autoSuggestions.province}`);
        }
      }
    } else if (field === 'city') {
      if (autoSuggestions.province) {
        const currentProvince = form.getValues(getFieldName('province_state') as any);
        if (!currentProvince || currentProvince !== autoSuggestions.province) {
          form.setValue(getFieldName('province_state') as any, autoSuggestions.province);
          console.log(`SmartAddressInput: Auto-populated province: ${autoSuggestions.province}`);
        }
      }
    }
    
    // Re-validate all fields after selection
    const allValues = form.getValues();
    validateAddressField(field, value, allValues);
  };

  // Render autocomplete input
  const renderAutocompleteField = (
    field: string, 
    label: string, 
    placeholder: string, 
    suggestions: string[],
    normalizer: (value: string) => string
  ) => {
    const fieldName = getFieldName(field);
    const validation = validationState[field];
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    
    return (
      <FormField
        control={form.control}
        name={fieldName}
        render={({ field: formField }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-1">
              {label} {required && <span className="text-destructive">*</span>}
              {validation?.warnings.length > 0 && (
                <AlertTriangle className="w-3 h-3 text-amber-500" />
              )}
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <div className="relative">
                      <Input
                        {...formField}
                        placeholder={placeholder}
                        onChange={(e) => {
                          formField.onChange(e);
                          const allValues = form.getValues();
                          validateAddressField(field, e.target.value, allValues);
                        }}
                        onFocus={() => {
                          // Only open popover when field is focused and has value with suggestions
                          if (suggestions.length > 0) {
                            setIsPopoverOpen(true);
                          }
                        }}
                        onBlur={(e) => {
                          formField.onBlur();
                          // Defer normalization to prevent focus/selection issues
                          const currentValue = e.target.value;
                          setTimeout(() => {
                            const normalized = normalizer(currentValue);
                            if (normalized && normalized !== currentValue) {
                              form.setValue(fieldName as any, normalized, { shouldValidate: false });
                            }
                          }, 0);
                          // Close popover when field loses focus
                          setTimeout(() => setIsPopoverOpen(false), 150);
                        }}
                        className={cn(
                          validation?.isValid === false && "border-amber-500",
                          validation?.warnings.length > 0 && validation.isValid && "border-blue-500"
                        )}
                      />
                    </div>
                  </PopoverTrigger>
                  {suggestions.length > 0 && (
                    <PopoverContent align="start" className="w-full p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
                      <Command shouldFilter={false}>
                        <CommandList>
                          {suggestions
                            .filter(s => 
                              !formField.value || 
                              s.toLowerCase().includes(formField.value.toLowerCase()) ||
                              formField.value.toLowerCase().includes(s.toLowerCase())
                            )
                            .slice(0, 8)
                            .map(suggestion => (
                            <CommandItem
                              key={suggestion}
                              onSelect={() => {
                                handleFieldSelect(field, suggestion);
                                setIsPopoverOpen(false);
                              }}
                              className="flex items-center justify-between cursor-pointer"
                            >
                              <div className="flex items-center">
                                <MapPin className="w-3 h-3 mr-2 text-muted-foreground" />
                                <span>{suggestion}</span>
                              </div>
                              {formField.value === suggestion && <Check className="w-3 h-3 text-primary" />}
                            </CommandItem>
                          ))}
                          {suggestions.filter(s => 
                            !formField.value || 
                            s.toLowerCase().includes(formField.value.toLowerCase()) ||
                            formField.value.toLowerCase().includes(s.toLowerCase())
                          ).length === 0 && formField.value && (
                            <div className="px-2 py-1 text-sm text-muted-foreground">
                              No suggestions found
                            </div>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  )}
                </Popover>
              </div>
            </FormControl>
            
            {/* Validation messages */}
            {validation?.warnings.length > 0 && (
              <div className="space-y-1">
                {validation.warnings.map((warning, idx) => (
                  <div key={idx} className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="w-3 h-3" />
                    {warning}
                  </div>
                ))}
                {validation.suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {validation.suggestions.map((suggestion, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground"
                        onClick={() => {
                          if (suggestion.startsWith('Change city to')) {
                            const cityName = suggestion.match(/"([^"]+)"/)?.[1];
                            if (cityName) handleFieldSelect('city', cityName);
                          }
                        }}
                      >
                        {suggestion}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {renderAutocompleteField(
          'street_address',
          'Street Address',
          '123 Main Street',
          [], // No autocomplete for street addresses
          normalizeString
        )}
        
        {renderAutocompleteField(
          'suburb',
          'Suburb',
          'Suburb name',
          enhancedSuggestions.suburbs,
          normalizeSuburb
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderAutocompleteField(
          'city',
          'City',
          'City name',
          enhancedSuggestions.cities,
          normalizeCity
        )}
        
        {renderAutocompleteField(
          'province_state',
          'Province/State',
          'Province or State',
          enhancedSuggestions.provinces,
          normalizeProvinceState
        )}
      </div>

      {renderAutocompleteField(
        'postal_code',
        'Postal Code',
        '12345',
        enhancedSuggestions.postalCodes,
        normalizePostalCode
      )}
    </div>
  );
}